import moment from "moment";
import { DateTimeProvider } from "../../Services/DateTimeProvider";
import { TimeUtils } from "../../Utils/TimeUtils";
import { Step } from "../Step/Step";
import { Cash } from "../Step/Cash";
import { StepEvent } from "../Step/StepEvent";
import { StepType } from "../Step/StepType";
import { UsageStepData } from "./Models/UsageStepData";
import { UsageStepState } from "./Models/UsageStepState";
import { Line, UnorderedList } from "@tblabs/truffle";
import { AgentsProvider } from "../../Services/Agents/AgentsProvider";
import { ToDo } from "../../Components/ToDo";
import { Text } from "@tblabs/truffle";


export class UsageStep extends Step<UsageStepState>
{
    public Type = StepType.Usage;
    public Icon = "🚀";
    public TitleForCustomer = () => `Użytkowanie`;
    public Title = () => `Użytkowanie`;

    public InitialState = UsageStepState.BeforeStart;
    public WellDoneStates = [UsageStepState.AfterEnd, UsageStepState.StoppedBeforePlannedEnd];
    public FailedDoneStates = [UsageStepState.Interrupted, UsageStepState.Omitted];

    public get AsEvents(): StepEvent[]
    {
        return [];
    }
    public get Mana(): Cash[]
    {
        return [];
    }
    public get WhenPlanned(): Date
    {
        return this.Data.Start.value;
    }
    public get WhenDone(): Date
    {
        return this.Data.End.value;
    }

    constructor(private _agent: AgentsProvider, public Data: UsageStepData)
    {
        super(Data);
    }

    public Init(): void
    {
        if (!this.IsDone)
        {
            if (moment(this.Data.Start.value).isBefore(moment(DateTimeProvider.Now)))
                this.GoTo(UsageStepState.InUse);

            if (moment(this.Data.End.value).isBefore(moment(DateTimeProvider.Now)))
                this.GoTo(UsageStepState.AfterEnd);
        }
    }

    public STATES = {
        [UsageStepState.BeforeStart]: {
            StateName: "Przed rozpoczęciem użytkowania",
            Timeout: () => this.Data.Start,
            Customer: {
                Content: () => [`✔️ Użytkowanie rozpocznie się ${TimeUtils.FormattedDate(this.Data.Start)}`],
            },
            Admin: {
                Content: () => [
                    `✔️ Rozpocznie się ${TimeUtils.TimeLeftTo(this.Data.Start)}`,
                    new Line(),
                    `Okres użytkowania:\n`,
                    new Text(this.Data.Start, v => TimeUtils.FormattedDateTime(v as Date)),
                    " - ",
                    new Text(this.Data.End, v => TimeUtils.FormattedDateTime(v as Date)),
                    `\nTeraz jest ${DateTimeProvider.NowAsString}`
                ],
                Actions: {
                    "✅": () => this.GoTo(UsageStepState.AfterEnd),
                    "✖️": () => this.GoTo(UsageStepState.Omitted),
                    "❌": () => this.GoTo(UsageStepState.Interrupted),
                },
            },
        },
        [UsageStepState.InUse]: {
            StateName: "W użyciu",
            Timeout: () => this.Data.End,
            Customer: {
                Content: () => [`Okres użytkowania trwa\nod ${ moment(this.Data.Start.value).format("dddd, DD.MM.YYYY")}\ndo ${moment(this.Data.End.value).format("dddd, DD.MM.YYYY")}`,
                // new Line(),
                // `W razie pytań dzwoń do swojego opiekuna:\n${this._agent.Get(this.Data.AssignedAgentId.value)?.NameAndPhone || Agent.Default.NameAndPhone}`
                ],
            },
            Admin: {
                Content: () => [
                    `☑️ ${TimeUtils.FormattedDateTime(this.Data.Start)} - ${TimeUtils.FormattedDateTime(this.Data.End)}`,
                    new Line().Margin(8),
                    `Urządzenia w użyciu (do ${TimeUtils.TimeLeftTo(this.Data.End)}):`,
                    new UnorderedList(this.Order?.BasketItems?.map(x => x.ProductSerialNumber.value)).Margin(4),
                    new Line().Margin(8),
                    `Użytkownik: ${this.Order?.People?.First()?.AsShortString || "Nieznany użytkownik"}`,
                    new ToDo(`Jeśli sprzęt został zwrócony - kliknij ✅\nJeśli doszło do awarii - kliknij ❌\nJeśli zwrócony przed czasem - kliknij 🛑`),
                ],
                Actions: {
                    "✅": () => this.GoTo(UsageStepState.AfterEnd),
                    "🛑": () => { this.Data.StoppedBeforePlannedEnd.value = DateTimeProvider.Now; this.GoTo(UsageStepState.StoppedBeforePlannedEnd); },
                    "❌": () => { this.Data.CancelationReason.value = "Awaria"; this.GoTo(UsageStepState.Interrupted); },
                },
            },
        },
        [UsageStepState.AfterEnd]: {
            StateName: "Użytkowanie zakończone",
            Customer: {
                Content: () => [`✅ Okres użytkowania zakończył się`],
            },
            Admin: {
                Content: () => [
                    `✅ Okres użytkowania zakończył się ${TimeUtils.TimeLeftTo(this.Data.End)}`,
                    new Line(),
                    `${TimeUtils.FormattedDateTime(this.Data.Start)} - ${TimeUtils.FormattedDateTime(this.Data.End)}`
                ],
            },
        },
        [UsageStepState.Omitted]: {
            StateName: "Użytkowanie pominięte",
            Customer: {
                Content: () => [`✖️ Sprzęt nie był używany`]
            },
            Admin: {
                Content: () => [
                    `✖️ Okres użytkowania został pominięty`
                ],
            },
        },
        [UsageStepState.StoppedBeforePlannedEnd]: {
            StateName: "Użytkowanie zakończone przed planowanym końcem",
            Customer: {
                Content: () => [`✖️ Użytkowanie zostało przerwane`]
            },
            Admin: {
                Content: () => [
                    `✖️ Użytkowanie zostało przerwane przed planowanym zakończeniem ${TimeUtils.FormattedDateTime(this.Data.StoppedBeforePlannedEnd.value)}`
                ],
            },
        },
        [UsageStepState.Interrupted]: {
            StateName: "Użytkowanie przerwane",
            Customer: {
                Content: () => [`❌ Użytkowanie zostało przerwane`]
            },
            Admin: {
                Content: () => [
                    `❌ Użytkowanie zostało przerwane z powodu: ${this.Data.CancelationReason.value || "(nie podano)"}`
                ],
            },
        },
    };
}
